import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Archive, ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Modal } from '../../componentes/sobreposicao';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { del, mensagemDeErro, patch, post } from '../../nucleo/api';
import { numero, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { CHAVE, SelosColuna, useQuadros, useRotuloColuna, useRotuloQuadro } from './comum';
import { ETAPAS, type Coluna, type Quadro } from './tipos';

type Edicao = { tipo: 'quadro'; quadro?: Quadro } | { tipo: 'coluna'; quadro: Quadro; coluna?: Coluna };

function FormEdicao({ edicao, aoSalvar }: { edicao: Edicao; aoSalvar: () => Promise<void> }) {
  const { t } = useTranslation('tarefas');
  const s = useSessao();
  if (edicao.tipo === 'quadro') {
    const campos: DefCampo[] = [
      { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true },
      { nome: 'descricao', rotulo: t('campos.descricao'), tipo: 'area' },
    ];
    return (
      <Formulario
        id="form-edicao-tarefas"
        campos={campos}
        inicial={edicao.quadro ? { nome: edicao.quadro.nome, descricao: edicao.quadro.descricao ?? '' } : {}}
        aoEnviar={async (v) => {
          const corpo = { nome: v.nome, descricao: typeof v.descricao === 'string' && v.descricao.trim() ? v.descricao : null };
          if (edicao.quadro) await patch(`/tarefas/quadros/${edicao.quadro.id}`, corpo);
          else await post('/tarefas/quadros', corpo);
          await aoSalvar();
        }}
      />
    );
  }
  const podeAprovar = s.pode('tarefas.aprovar');
  const c = edicao.coluna;
  const campos: DefCampo[] = [
    { nome: 'nome', rotulo: t('campos.nome'), tipo: 'texto', obrigatorio: true },
    { nome: 'etapa', rotulo: t('campos.etapa'), tipo: 'selecao', obrigatorio: true, opcoes: ETAPAS.map((e) => ({ valor: e, rotulo: t(`etapas.${e}`) })) },
    { nome: 'limite_wip', rotulo: t('campos.limiteWip'), tipo: 'numero', min: 1, max: 999, ajuda: t('campos.limiteWipAjuda'), largura: 'meia' },
    { nome: 'espera_pessoa', rotulo: t('campos.esperaPessoa'), tipo: 'booleano', largura: 'meia' },
    { nome: 'exige_aprovacao', rotulo: t('campos.exigeAprovacao'), tipo: 'booleano', desabilitado: !podeAprovar, ajuda: t('quadros.aprovacaoTexto') },
  ];
  return (
    <Formulario
      id="form-edicao-tarefas"
      campos={campos}
      inicial={c ? { ...c } : { etapa: 'fila', espera_pessoa: false, exige_aprovacao: false }}
      aoEnviar={async (v) => {
        const limite = typeof v.limite_wip === 'number' && v.limite_wip > 0 ? v.limite_wip : null;
        const corpo = { nome: v.nome, etapa: v.etapa, limite_wip: limite, espera_pessoa: !!v.espera_pessoa, exige_aprovacao: !!v.exige_aprovacao };
        if (c) await patch(`/tarefas/colunas/${c.id}`, corpo);
        else await post(`/tarefas/quadros/${edicao.quadro.id}/colunas`, corpo);
        await aoSalvar();
      }}
    />
  );
}

/** Aba Quadros: quadros extras livres e as colunas de cada um (etapa, limite de WIP, aprovação e espera de decisão). */
export function Quadros() {
  const { t } = useTranslation('tarefas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const rotuloColuna = useRotuloColuna();
  const rotuloQuadro = useRotuloQuadro();
  const quadros = useQuadros();
  const [edicao, setEdicao] = useState<Edicao | null>(null);
  const [arquivar, setArquivar] = useState<Quadro | null>(null);
  const [excluir, setExcluir] = useState<Coluna | null>(null);
  // Val5 (29/10): quadros e colunas são configuração (`tarefas.configurar`), inclusive arquivar o quadro; a lixeira segue com `tarefas.excluir`.
  const podeEditar = s.pode('tarefas.configurar');
  const recarregar = () => qc.invalidateQueries({ queryKey: CHAVE });
  const erro = (e: unknown) => toast.erro(mensagemDeErro(e));
  const reordenar = useMutation({ mutationFn: (v: { quadro: string; ids: string[] }) => post(`/tarefas/quadros/${v.quadro}/colunas/reordenar`, { ids: v.ids }), onSuccess: recarregar, onError: erro });
  const mArquivar = useMutation({
    mutationFn: (id: string) => del(`/tarefas/quadros/${id}`),
    onSuccess: async () => {
      setArquivar(null);
      await recarregar();
    },
    onError: (e) => {
      setArquivar(null);
      erro(e);
    },
  });
  const mExcluir = useMutation({
    mutationFn: (id: string) => del(`/tarefas/colunas/${id}`),
    onSuccess: async () => {
      setExcluir(null);
      await recarregar();
    },
    onError: (e) => {
      setExcluir(null);
      erro(e);
    },
  });
  const mover = (q: Quadro, i: number, d: -1 | 1) => {
    const ids = q.colunas.map((c) => c.id);
    [ids[i], ids[i + d]] = [ids[i + d]!, ids[i]!];
    reordenar.mutate({ quadro: q.id, ids });
  };
  const tituloEdicao = !edicao ? '' : edicao.tipo === 'quadro' ? (edicao.quadro ? t('acoes.editarQuadro') : t('quadros.novoTitulo')) : edicao.coluna ? t('acoes.editarColuna') : t('acoes.novaColuna');

  return (
    <>
      {podeEditar && (
        <AcaoPrimaria>
          {s.pode('tarefas.excluir') && <BotaoLixeira entidades={['tarefas.quadro', 'tarefas.card']} />}
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setEdicao({ tipo: 'quadro' })}>
            {t('acoes.novoQuadro')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Estado consulta={quadros}>
        {(d) =>
          d.itens.length === 0 ? (
            <Cartao semPadding>
              <Vazio texto={t('quadros.vazio')} />
            </Cartao>
          ) : (
            <div className="flex flex-col gap-4">
              {d.itens.map((q) => (
                <Cartao
                  key={q.id}
                  titulo={
                    <span className="flex flex-wrap items-center gap-2">
                      {/* Review r16 robustez B4: nome longo sem espaço cortado com o `title` (alargava a tela a 390). */}
                      <TextoCortado className="min-w-0 max-w-full">{rotuloQuadro(q)}</TextoCortado>
                      <span className="text-xs font-normal text-tinta-3">{t('quadros.abertos', { count: q.abertos })}</span>
                    </span>
                  }
                  acao={
                    podeEditar ? (
                      <span className="flex gap-1">
                        <Botao tamanho="sm" variante="fantasma" icone={<Pencil className="size-4" />} onClick={() => setEdicao({ tipo: 'quadro', quadro: q })}>
                          <span className="sr-only sm:not-sr-only">{t('acoes.editarQuadro')}</span>
                        </Botao>
                        {podeEditar && (
                          <Botao tamanho="sm" variante="perigo" icone={<Archive className="size-4" />} onClick={() => setArquivar(q)}>
                            <span className="sr-only sm:not-sr-only">{t('acoes.arquivarQuadro')}</span>
                          </Botao>
                        )}
                      </span>
                    ) : undefined
                  }
                >
                  {q.descricao && <p className="mb-3 text-sm text-tinta-2 break-words">{textoLivre(q.descricao)}</p>}
                  <ol className="flex flex-col divide-y divide-borda">
                    {q.colunas.map((c, i) => (
                      <li key={c.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2">
                        <span className="numero w-6 text-xs text-tinta-3">{i + 1}</span>
                        <span className="flex min-w-0 flex-1 flex-col gap-1">
                          <TextoCortado className="text-sm font-medium">{rotuloColuna(c)}</TextoCortado>
                          <span className="flex flex-wrap items-center gap-1 text-xs text-tinta-3">
                            <span>{t(`etapas.${c.etapa}`)}</span>
                            <span aria-hidden>·</span>
                            <span>{c.limite_wip !== null ? t('kanban.wip', { total: numero(c.total), limite: numero(c.limite_wip) }) : t('kanban.semLimite', { count: c.total, total: numero(c.total) })}</span>
                          </span>
                          {(c.exige_aprovacao || c.espera_pessoa) && (
                            <span className="flex flex-wrap gap-1">
                              <SelosColuna c={c} />
                            </span>
                          )}
                        </span>
                        {podeEditar && (
                          <span className="flex items-center gap-1">
                            <Botao tamanho="sm" variante="fantasma" aria-label={t('acoes.subir', { nome: rotuloColuna(c) })} disabled={i === 0 || reordenar.isPending} onClick={() => mover(q, i, -1)} icone={<ArrowUp className="size-4" />} />
                            <Botao tamanho="sm" variante="fantasma" aria-label={t('acoes.descer', { nome: rotuloColuna(c) })} disabled={i === q.colunas.length - 1 || reordenar.isPending} onClick={() => mover(q, i, 1)} icone={<ArrowDown className="size-4" />} />
                            <Botao tamanho="sm" variante="fantasma" aria-label={`${t('acoes.editarColuna')}: ${rotuloColuna(c)}`} onClick={() => setEdicao({ tipo: 'coluna', quadro: q, coluna: c })} icone={<Pencil className="size-4" />} />
                            <Botao tamanho="sm" variante="fantasma" aria-label={`${t('acoes.excluirColuna')}: ${rotuloColuna(c)}`} onClick={() => setExcluir(c)} icone={<Trash2 className="size-4" />} />
                          </span>
                        )}
                      </li>
                    ))}
                  </ol>
                  {podeEditar && (
                    <Botao className="mt-3" tamanho="sm" icone={<Plus className="size-4" />} onClick={() => setEdicao({ tipo: 'coluna', quadro: q })}>
                      {t('acoes.novaColuna')}
                    </Botao>
                  )}
                  {q.colunas.some((c) => c.exige_aprovacao) && (
                    <p className="mt-3 flex flex-wrap items-center gap-2 text-xs text-tinta-3">
                      <Selo cor="roxo">{t('kanban.aprovacao')}</Selo>
                      {t('quadros.aprovacaoTexto')}
                    </p>
                  )}
                </Cartao>
              ))}
            </div>
          )
        }
      </Estado>
      <Modal
        aberto={!!edicao}
        aoFechar={() => setEdicao(null)}
        titulo={tituloEdicao}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setEdicao(null)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-edicao-tarefas">
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        {edicao?.tipo === 'quadro' && !edicao.quadro && <p className="mb-3 text-sm text-tinta-3">{t('quadros.novoTexto')}</p>}
        {edicao && (
          <FormEdicao
            key={edicao.tipo === 'quadro' ? (edicao.quadro?.id ?? 'novo-quadro') : (edicao.coluna?.id ?? 'nova-coluna')}
            edicao={edicao}
            aoSalvar={async () => {
              setEdicao(null);
              toast.sucesso(tn('comum.salvo'));
              await recarregar();
            }}
          />
        )}
      </Modal>
      <Confirmar
        aberto={!!arquivar}
        aoFechar={() => setArquivar(null)}
        aoConfirmar={() => arquivar && mArquivar.mutate(arquivar.id)}
        titulo={t('quadros.arquivarTitulo')}
        texto={t('quadros.arquivarTexto')}
        perigoso
        carregando={mArquivar.isPending}
      />
      <Confirmar
        aberto={!!excluir}
        aoFechar={() => setExcluir(null)}
        aoConfirmar={() => excluir && mExcluir.mutate(excluir.id)}
        titulo={t('quadros.excluirColunaTitulo')}
        texto={t('quadros.excluirColunaTexto')}
        perigoso
        carregando={mExcluir.isPending}
      />
    </>
  );
}
